// Global Chartkick ↔ Turbo Drive lifecycle bridge.
//
// Why this exists (root-caused June 2026; the exhibitor dashboard went blank
// after an admin "Login As" masquerade):
//
// Chartkick's dist self-registers a global `turbo:before-render` listener
// that calls `Chartkick.destroyAll()` (gated on
// `Chartkick.config.autoDestroy !== false`). On a NORMAL Turbo Drive visit
// that's safe: destroyAll fires before the body swap, then the swapped-in
// body's inline Chartkick helper IIFEs re-create the charts.
//
// But when the visit's server response arrives via an HTTP redirect chain
// (e.g. masquerade: /exhibitor_users/masquerade → 302 / → 302
// /exhibitor/dashboard, or any link to "/"), Turbo 7.3 follows up with an
// INTERNAL second visit to fix the address bar — `Visit.complete()` →
// `followRedirect()` proposes `{ action: "replace", response, willRender:
// false }`. That ghost visit still runs `View.render()` (its `shouldRender`
// never consults `willRender`), so `turbo:before-render` fires a SECOND time
// and Chartkick destroys the charts created moments earlier — but
// `PageRenderer.render()` gates the body swap on `willRender`, so the inline
// init scripts never re-execute. Net: every Chartkick chart on the landing
// page is a dead, blank canvas. The same destroy-without-rebuild gap hits
// Turbo history restorations (back/forward) on Chartkick pages.
//
// Fix: after every Drive render (`turbo:render` fires AFTER Chartkick's
// destroyAll), re-execute the Chartkick helper init script for any chart
// whose container is still in the DOM but whose
// `window.Chartkick.charts` registry entry is gone. Charts that are alive
// are left strictly alone (re-running their IIFE would make Chart.js throw
// "Canvas is already in use"), so the normal navigation path is untouched.
//
// Scope guards:
//   * `.reports-page` is EXCLUDED — reports_page_controller.js is the single
//     owner of the Reports Hub chart lifecycle (it has additional Turbo
//     Frame concerns: lazy tab frames, before-cache disposal). See its
//     header comment for the measured event order this module relies on.
//   * Preview renders are skipped (`html[data-turbo-preview]`), mirroring
//     the guard inside Chartkick's own helper IIFE — the final render that
//     follows a preview triggers `turbo:render` again and rebuilds then.
//   * Only inline, classic-JS, `data-turbo-eval`-honoring scripts that
//     lexically contain a Chartkick helper initializer are considered —
//     never arbitrary page scripts (re-running those would stack jQuery
//     handlers etc.; see the warning in packs/application.js).

// Matches the chartkick gem helper output, e.g.
//   new Chartkick["ColumnChart"]("chart-1", [...], {...});
// and captures the chart id as a JSON string literal (the helper emits it
// via `element_id.to_json` + json_escape, so JSON.parse decodes it exactly).
//
// ASSUMPTION: an eligible script is a single-init helper IIFE — the rebuild
// re-executes the WHOLE script text. Hand-written inline scripts that mix a
// bracket-form Chartkick init with other side-effecting statements would have
// those statements re-run on every rebuild; keep such scripts dot-form
// (new Chartkick.ColumnChart(...)) or single-purpose. (The one dot-form init
// at exhibitor/users/show is deliberately NOT covered by this bridge.)
const CHARTKICK_INIT_RE = /new Chartkick\[\s*"[A-Za-z]+"\s*\]\(\s*("(?:[^"\\]|\\.)*")/

export function chartkickChartIdFromScript(script) {
  const match = (script.text || "").match(CHARTKICK_INIT_RE)
  if (!match) return null
  try {
    return JSON.parse(match[1])
  } catch (_e) {
    return null
  }
}

function isEligibleInlineScript(script) {
  if (script.dataset && script.dataset.turboEval === "false") return false
  const type = (script.type || "").trim().toLowerCase()
  return type === "" || type === "text/javascript" || type === "application/javascript"
}

export function rebuildOrphanedChartkickCharts() {
  const Chartkick = window.Chartkick
  if (!Chartkick || !Chartkick.charts) return
  // Don't build charts into a transient preview snapshot — Chartkick's own
  // helper IIFE bails on this attribute for the same reason.
  if (document.documentElement.hasAttribute("data-turbo-preview")) return

  for (const script of document.querySelectorAll("script:not([src])")) {
    if (!isEligibleInlineScript(script)) continue
    // Reports Hub charts have their own lifecycle owner.
    if (script.closest(".reports-page")) continue

    const chartId = chartkickChartIdFromScript(script)
    if (!chartId) continue
    if (Chartkick.charts[chartId]) continue // chart is alive — never rebuild
    if (!document.getElementById(chartId)) continue // container gone

    // Re-execute the helper IIFE by swapping in a fresh script element
    // (same pattern as reports_page_controller.js). Preserve the CSP nonce.
    const newScript = document.createElement("script")
    newScript.text = script.text
    if (script.nonce) newScript.nonce = script.nonce
    script.replaceWith(newScript)
  }
}

export function startChartkickTurboLifecycle() {
  // `turbo:render` fires once per Drive render, AFTER Chartkick's global
  // destroyAll (`turbo:before-render`) — including the willRender:false
  // ghost render of a followed redirect and history restorations.
  document.addEventListener("turbo:render", rebuildOrphanedChartkickCharts)
}
